import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    FormControl,
    Grid,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { API_ENDPOINTS } from '../../utils/config';
import { THEME } from '../../theme';
import LogisticsNetworkCanvas from './LogisticsNetworkCanvas';
import SolverResultVisual from './SolverVisualizations';

const panelSx = {
    bgcolor: THEME.cardBg,
    border: `1px solid ${THEME.border}`,
    borderRadius: 2,
    p: 2,
};

const selectSx = {
    color: THEME.textMain,
    bgcolor: '#121214',
    '.MuiOutlinedInput-notchedOutline': { borderColor: THEME.border },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: THEME.primary },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: THEME.primary },
    '.MuiSvgIcon-root': { color: THEME.secondary },
};

const FIELD_DEFAULTS = {
    eoq: { D: 1200, K: 50, h: 2.5 },
    sde_routing: {
        n_stores: 3,
        T: 10,
        dt: 0.1,
        mu: 1.2,
        sigma: 0.4,
        h: 0.5,
        p: 4,
        c: 8,
        W: 6,
        I0: 5,
        I_min: 1,
        n_paths: 40,
        seed: 42,
    },
    cvrp: { vehicle_capacity: 50 },
};

const NETWORK_PROBLEMS = new Set(['transshipment', 'facility_location', 'cvrp']);

export default function ResourceMgmtPanel() {
    const [problem, setProblem] = useState('eoq');
    const [schemas, setSchemas] = useState([]);
    const [params, setParams] = useState(FIELD_DEFAULTS.eoq);
    const [graph, setGraph] = useState({ nodes: [], edges: [] });
    const [loading, setLoading] = useState(false);
    const [healthy, setHealthy] = useState(null);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [result, setResult] = useState(null);

    const API = API_ENDPOINTS.RESOURCE_MGMT;

    const checkHealth = useCallback(async () => {
        try {
            const resp = await fetch(`${API}/health`);
            const data = await resp.json();
            setHealthy(resp.ok && data.status === 'healthy');
            return data;
        } catch {
            setHealthy(false);
            return null;
        }
    }, [API]);

    useEffect(() => {
        checkHealth();
        fetch(`${API}/schemas`)
            .then((r) => r.json())
            .then((data) => {
                if (data.problems) setSchemas(data.problems);
            })
            .catch(() => {});
    }, [API, checkHealth]);

    useEffect(() => {
        setParams(FIELD_DEFAULTS[problem] || {});
        setResult(null);
        setError(null);
        setSuccess(null);
    }, [problem]);

    const schema = useMemo(
        () => schemas.find((s) => s.id === problem),
        [schemas, problem]
    );

    const handleGraphChange = useCallback((next) => {
        setGraph(next);
    }, []);

    const runSolver = async () => {
        setLoading(true);
        setError(null);
        setSuccess(null);
        setResult(null);
        try {
            const health = await checkHealth();
            if (!health) {
                // Dev: start server_resource_mgmt.py if health check fails locally.
                setError("Server not responding. Try again later.");
                return;
            }
            const body = {
                problem,
                params,
                nodes: graph.nodes,
                edges: graph.edges,
            };
            const resp = await fetch(`${API}/solve`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
            const data = await resp.json();
            if (!resp.ok || data.success === false) {
                console.error('Logistics solve failed:', data.error || data);
                setError('Solve failed. Check your inputs and try again.');
                return;
            }
            setResult(data);
            setSuccess(data.summary || 'Done.');
        } catch (err) {
            console.error(err);
            setError('Could not reach the logistics services.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box>
            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={4}>
                    <Typography variant="caption" color={THEME.secondary}>
                        Problem
                    </Typography>
                    <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
                        <Select
                            value={problem}
                            onChange={(e) => setProblem(e.target.value)}
                            sx={selectSx}
                        >
                            {(schemas.length
                                ? schemas
                                : [
                                      { id: 'eoq', label: 'Economic Order Quantity (EOQ)' },
                                      { id: 'transshipment', label: 'Transshipment' },
                                      { id: 'facility_location', label: 'Facility Location' },
                                      { id: 'cvrp', label: 'CVRP' },
                                      { id: 'sde_routing', label: 'SDE-constrained routing' },
                                  ]
                            ).map((p) => (
                                <MenuItem key={p.id} value={p.id}>
                                    {p.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} md={5} sx={{ display: 'flex', alignItems: 'flex-end' }}>
                    <Typography variant="body2" color={THEME.secondary}>
                        {schema?.description ||
                            'Configure parameters or the network, then run the solver.'}
                    </Typography>
                </Grid>
                <Grid item xs={12} md={3} sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
                    <Chip
                        size="small"
                        icon={<LocalShippingIcon />}
                        label={healthy ? 'Online' : 'Offline'}
                        sx={{
                            bgcolor: healthy ? 'rgba(44,187,93,0.15)' : 'rgba(239,71,67,0.15)',
                            color: healthy ? THEME.primary : THEME.accent,
                            border: `1px solid ${healthy ? THEME.primary : THEME.accent}`,
                        }}
                    />
                    <Button
                        fullWidth
                        variant="contained"
                        startIcon={
                            loading ? (
                                <CircularProgress size={16} color="inherit" />
                            ) : (
                                <PlayArrowIcon />
                            )
                        }
                        onClick={runSolver}
                        disabled={loading}
                        sx={{
                            bgcolor: THEME.primary,
                            color: '#000',
                            fontWeight: 700,
                            textTransform: 'none',
                            '&:hover': { bgcolor: THEME.primaryHover },
                        }}
                    >
                        {loading ? 'Running…' : 'Run'}
                    </Button>
                </Grid>
            </Grid>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}
            {success && (
                <Alert severity="success" sx={{ mb: 2 }}>
                    {success}
                </Alert>
            )}

            <Grid container spacing={2}>
                <Grid item xs={12} md={NETWORK_PROBLEMS.has(problem) ? 12 : 5}>
                    <Box sx={panelSx}>
                        <Typography
                            variant="subtitle2"
                            sx={{ color: THEME.primary, mb: 1.5, letterSpacing: 1 }}
                        >
                            <b>Configuration</b>
                        </Typography>

                        {!NETWORK_PROBLEMS.has(problem) && (
                            <Stack spacing={1.5}>
                                {Object.entries(params).map(([key, value]) => (
                                    <TextField
                                        key={key}
                                        size="small"
                                        type="number"
                                        label={key}
                                        value={value}
                                        onChange={(e) =>
                                            setParams((prev) => ({
                                                ...prev,
                                                [key]:
                                                    e.target.value === ''
                                                        ? ''
                                                        : Number(e.target.value),
                                            }))
                                        }
                                        InputLabelProps={{ sx: { color: THEME.secondary } }}
                                        InputProps={{ sx: { color: THEME.textMain } }}
                                        sx={{
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: THEME.border,
                                            },
                                        }}
                                    />
                                ))}
                            </Stack>
                        )}

                        {problem === 'cvrp' && (
                            <TextField
                                size="small"
                                type="number"
                                label="Vehicle capacity Q"
                                value={params.vehicle_capacity ?? 50}
                                onChange={(e) =>
                                    setParams((prev) => ({
                                        ...prev,
                                        vehicle_capacity: Number(e.target.value),
                                    }))
                                }
                                InputLabelProps={{ sx: { color: THEME.secondary } }}
                                InputProps={{ sx: { color: THEME.textMain } }}
                                sx={{
                                    mb: 2,
                                    maxWidth: 280,
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: THEME.border,
                                    },
                                }}
                            />
                        )}

                        {NETWORK_PROBLEMS.has(problem) && (
                            <>
                                <Typography variant="body2" color={THEME.secondary} sx={{ mb: 1 }}>
                                    Drag nodes, add roles, and connect edges (costs). Distance is
                                    used when an edge cost is omitted.
                                </Typography>
                                <LogisticsNetworkCanvas
                                    problem={problem}
                                    onGraphChange={handleGraphChange}
                                    optimalEdgeIds={result?.optimal_edge_ids}
                                    optimalNodeIds={result?.opened_ids}
                                    routes={result?.routes}
                                    shipments={result?.shipments}
                                    allocations={result?.allocations}
                                />
                            </>
                        )}
                    </Box>
                </Grid>

                <Grid item xs={12} md={NETWORK_PROBLEMS.has(problem) ? 12 : 7}>
                    <Box sx={panelSx}>
                        <Typography
                            variant="subtitle2"
                            sx={{ color: THEME.primary, mb: 1.5, letterSpacing: 1 }}
                        >
                            <b>Results</b>
                        </Typography>
                        {!result ? (
                            <Box
                                sx={{
                                    minHeight: 180,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: `1px dashed ${THEME.border}`,
                                    borderRadius: 2,
                                    color: THEME.secondary,
                                    px: 2,
                                    textAlign: 'center',
                                }}
                            >
                                Run a problem to see optimal decisions and costs.
                            </Box>
                        ) : (
                            <Stack spacing={1.5}>
                                {result.metrics && (
                                    <Typography variant="body2" color={THEME.secondary}>
                                        {Object.entries(result.metrics)
                                            .map(
                                                ([k, v]) =>
                                                    `${k}: ${
                                                        typeof v === 'number' ? v.toFixed?.(4) ?? v : v
                                                    }`
                                            )
                                            .join(' · ')}
                                    </Typography>
                                )}
                                {result.Q_star != null && (
                                    <Typography variant="body1" color={THEME.textMain}>
                                        Q* = {Number(result.Q_star).toFixed(4)} · TC ={' '}
                                        {Number(result.total_cost).toFixed(4)}
                                    </Typography>
                                )}
                                {result.opened && (
                                    <Typography variant="body2" color={THEME.secondary}>
                                        Opened: {result.opened.join(', ')}
                                    </Typography>
                                )}
                                {result.shipments && (
                                    <Box component="ul" sx={{ m: 0, pl: 2, color: THEME.secondary }}>
                                        {result.shipments.slice(0, 12).map((s, i) => (
                                            <li key={i}>
                                                {s.from} → {s.to}: {Number(s.qty).toFixed(2)}
                                            </li>
                                        ))}
                                    </Box>
                                )}
                                {result.allocations && (
                                    <Box component="ul" sx={{ m: 0, pl: 2, color: THEME.secondary }}>
                                        {result.allocations.slice(0, 12).map((a, i) => (
                                            <li key={i}>
                                                {a.facility} → {a.customer}:{' '}
                                                {(a.fraction * 100).toFixed(1)}%
                                            </li>
                                        ))}
                                    </Box>
                                )}
                                {result.routes && (
                                    <Stack spacing={0.5}>
                                        {result.routes.map((r) => (
                                            <Typography
                                                key={r.vehicle}
                                                variant="body2"
                                                color={THEME.secondary}
                                            >
                                                V{r.vehicle} (load {Number(r.load).toFixed(1)}):{' '}
                                                {r.path.join(' → ')}
                                            </Typography>
                                        ))}
                                    </Stack>
                                )}
                                {result.dispatch_count != null && (
                                    <Typography variant="body2" color={THEME.secondary}>
                                        Dispatches: {result.dispatch_count}
                                        {result.dispatch_times?.length
                                            ? ` at t ≈ ${result.dispatch_times
                                                  .slice(0, 8)
                                                  .map((t) => Number(t).toFixed(1))
                                                  .join(', ')}`
                                            : ''}
                                    </Typography>
                                )}

                                <SolverResultVisual
                                    problem={problem}
                                    result={result}
                                    params={params}
                                />

                                {result.solve_time != null && (
                                    <Typography variant="caption" color={THEME.secondary}>
                                        Solved in {result.solve_time}s
                                    </Typography>
                                )}
                            </Stack>
                        )}
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
}
